import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const TOKEN_KEY = 'bluespark_admin_token';

const apiClient = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Attach bearer token if present
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto-logout on 401 for admin routes
apiClient.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
    }
    return Promise.reject(error);
  }
);

// ===== Auth =====
export const authAPI = {
  login: async (email, password) => {
    const { data } = await apiClient.post('/auth/login', { email, password });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem('bluespark_admin_email', data.email);
    return data;
  },
  me: async () => {
    const { data } = await apiClient.get('/auth/me');
    return data;
  },
  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('bluespark_admin_email');
  },
  isAuthenticated: () => !!localStorage.getItem(TOKEN_KEY),
};

// ===== Products =====
export const productsAPI = {
  getAll: async (params = {}) => {
    const { data } = await apiClient.get('/products', { params });
    return data; // {products, total, page, limit, pages}
  },
  getList: async (params = {}) => {
    // Convenience: returns just the array (used by public pages)
    const { data } = await apiClient.get('/products', { params });
    return data.products;
  },
  getById: async (id) => {
    const { data } = await apiClient.get(`/products/${id}`);
    return data;
  },
  create: async (product) => {
    const { data } = await apiClient.post('/products', product);
    return data;
  },
  update: async (id, updates) => {
    const { data } = await apiClient.put(`/products/${id}`, updates);
    return data;
  },
  delete: async (id) => {
    const { data } = await apiClient.delete(`/products/${id}`);
    return data;
  },
  bulkUpsert: async (rows, createMissing = true) => {
    const { data } = await apiClient.post('/admin/products/bulk-upsert', {
      rows,
      create_missing: createMissing,
    });
    return data;
  },
  bulkPriceUpdate: async (updates) => {
    const { data } = await apiClient.put('/admin/products/bulk-price-update', { updates });
    return data;
  },
};

// ===== Reviews =====
export const reviewsAPI = {
  getAll: async (params = {}) => {
    const { data } = await apiClient.get('/reviews', { params });
    return data.reviews;
  },
  create: async (review) => {
    const { data } = await apiClient.post('/reviews', review);
    return data;
  },
  update: async (id, updates) => {
    const { data } = await apiClient.put(`/reviews/${id}`, updates);
    return data;
  },
  delete: async (id) => {
    const { data } = await apiClient.delete(`/reviews/${id}`);
    return data;
  },
};

// ===== Categories =====
export const categoriesAPI = {
  getAll: async () => {
    const { data } = await apiClient.get('/categories');
    return data.categories;
  },
  create: async (payload) => {
    const { data } = await apiClient.post('/categories', payload);
    return data;
  },
  update: async (id, updates) => {
    const { data } = await apiClient.put(`/categories/${id}`, updates);
    return data;
  },
  delete: async (id) => {
    const { data } = await apiClient.delete(`/categories/${id}`);
    return data;
  },
};

// ===== Makes =====
export const makesAPI = {
  getAll: async () => {
    const { data } = await apiClient.get('/makes');
    return data.makes;
  },
};

// ===== Models =====
export const modelsAPI = {
  getAll: async (make) => {
    const { data } = await apiClient.get('/models', { params: make ? { make } : {} });
    return data.models;
  },
};

// ===== Engines =====
export const enginesAPI = {
  getAll: async (make, model) => {
    const params = {};
    if (make) params.make = make;
    if (model) params.model = model;
    const { data } = await apiClient.get('/engines', { params });
    return data.engines;
  },
  getCCs: async (make, model) => {
    const params = {};
    if (make) params.make = make;
    if (model) params.model = model;
    const { data } = await apiClient.get('/engine-ccs', { params });
    return data.ccs;
  },
};

// ===== FAQs =====
export const faqsAPI = {
  getAll: async () => {
    const { data } = await apiClient.get('/faqs');
    return data.faqs;
  },
  create: async (section) => {
    const { data } = await apiClient.post('/faqs', section);
    return data;
  },
  update: async (id, updates) => {
    const { data } = await apiClient.put(`/faqs/${id}`, updates);
    return data;
  },
  delete: async (id) => {
    const { data } = await apiClient.delete(`/faqs/${id}`);
    return data;
  },
};

// ===== Carousel =====
export const carouselAPI = {
  getAll: async (activeOnly = false) => {
    const { data } = await apiClient.get('/carousel', {
      params: { active_only: activeOnly },
    });
    return data.slides;
  },
  create: async (slide) => {
    const { data } = await apiClient.post('/carousel', slide);
    return data;
  },
  update: async (id, updates) => {
    const { data } = await apiClient.put(`/carousel/${id}`, updates);
    return data;
  },
  delete: async (id) => {
    const { data } = await apiClient.delete(`/carousel/${id}`);
    return data;
  },
};

// ===== Admin Stats =====
export const adminAPI = {
  getStats: async () => {
    const { data } = await apiClient.get('/admin/stats');
    return data;
  },
  uploadPdf: async (file) => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post('/admin/upload/pdf', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data; // { url, size, filename }
  },
  uploadImage: async (file) => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post('/admin/upload/image', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
};

// ===== Admin Orders =====
export const adminOrdersAPI = {
  list: async (params = {}) => {
    const { data } = await apiClient.get('/admin/orders', { params });
    return data; // { orders, total, page, limit, pages }
  },
  get: async (id) => {
    const { data } = await apiClient.get(`/admin/orders/${id}`);
    return data;
  },
  updateFulfilment: async (id, { fulfilment_status, tracking_number, carrier } = {}) => {
    const body = { fulfilment_status };
    if (tracking_number !== undefined) body.tracking_number = tracking_number;
    if (carrier !== undefined) body.carrier = carrier;
    const { data } = await apiClient.patch(`/admin/orders/${id}`, body);
    return data;
  },
  refund: async (id, { amount, reason } = {}) => {
    const body = {};
    if (amount !== undefined && amount !== null && amount !== '') body.amount = Number(amount);
    if (reason) body.reason = reason;
    const { data } = await apiClient.post(`/admin/orders/${id}/refund`, body);
    return data;
  },
  runRecoveryScan: async () => {
    const { data } = await apiClient.post('/admin/recovery/scan');
    return data;
  },
  resendEmails: async (id) => {
    const { data } = await apiClient.post(`/admin/orders/${id}/resend-emails`);
    return data;
  },
  exportCsvUrl: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    const token = localStorage.getItem(TOKEN_KEY) || '';
    // Server requires auth header — caller should use fetch with Authorization
    return { url: `${API_URL}/admin/orders/export.csv${qs ? `?${qs}` : ''}`, token };
  },
  downloadCsv: async (params = {}) => {
    const response = await apiClient.get('/admin/orders/export.csv', {
      params,
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bluespark-orders-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

// ===== Admin Sales =====
export const adminSalesAPI = {
  summary: async ({ months = 12, month = null, start = null, end = null } = {}) => {
    let params;
    if (start && end) params = { start, end };
    else if (month) params = { month };
    else params = { months };
    const { data } = await apiClient.get('/admin/sales/summary', { params });
    return data;
  },
};

// ===== Editable static pages (Terms of Service, etc.) =====
export const pagesAPI = {
  get: async (slug) => {
    const { data } = await apiClient.get(`/pages/${slug}`);
    return data;
  },
  adminUpdate: async (slug, payload) => {
    const { data } = await apiClient.patch(`/admin/pages/${slug}`, payload);
    return data;
  },
};

// ===== Site Links (banner / footer / etc. — editable from admin) =====
export const siteLinksAPI = {
  getPublic: async () => {
    const { data } = await apiClient.get('/site-links');
    return data; // { banner: [], footer_quick: [], ... }
  },
  adminList: async () => {
    const { data } = await apiClient.get('/admin/site-links');
    return data;
  },
  create: async (payload) => {
    const { data } = await apiClient.post('/admin/site-links', payload);
    return data;
  },
  update: async (id, payload) => {
    const { data } = await apiClient.patch(`/admin/site-links/${id}`, payload);
    return data;
  },
  remove: async (id) => {
    const { data } = await apiClient.delete(`/admin/site-links/${id}`);
    return data;
  },
  reorder: async (group, items) => {
    const { data } = await apiClient.post('/admin/site-links/reorder', { group, items });
    return data;
  },
};

// ===== Stripe Checkout =====
export const checkoutAPI = {
  getAddons: async () => {
    const { data } = await apiClient.get('/addons');
    return data; // { bluetooth: { id, name, description, price } }
  },
  createSession: async (items, currency = 'aud', { addBluetooth = false } = {}) => {
    const origin_url = window.location.origin;
    const { data } = await apiClient.post('/checkout/session', {
      items, origin_url, currency,
      add_bluetooth: !!addBluetooth,
    });
    return data; // { url, session_id, amount, currency }
  },
  getStatus: async (sessionId) => {
    const { data } = await apiClient.get(`/checkout/status/${sessionId}`);
    return data;
  },
};

// ===== Orders =====
export const ordersAPI = {
  create: async (order) => {
    const { data } = await apiClient.post('/orders', order);
    return data;
  },
};

export default apiClient;
